export interface SetupGuide {
  title: string;
  steps: string[];
  links: { label: string; url: string }[];
  command?: (ctx: { slug: string }) => string;
  editPath?: string;
}

// Stesso testo nella guida domains, nel messaggio di chiusura di SetupPage e in finish-setup.sh.
export const CLOSE_PORTS_HINT =
  "Se l'istanza NON usa il Security Group dello stack (WebSg), chiudi 8443 e 22 nel suo Security Group (console EC2 → Security Groups) oppure usa `adopt-instance.sh … --replace-sg`.";

// I segreti si mettono in Secrets Manager DA FILE sulla EC2 (mai in chiaro in
// argv/history, mai dal browser). Poi il backend va riavviato.
const putSecretFromFile = (slug: string, key: string, file: string) =>
  [
    `# sulla EC2 (aws ssm start-session --target <InstanceId>):`,
    `sudo /opt/${slug}/bin/secret-set.sh ${key} ${file}`,
    `sudo /opt/${slug}/bin/restart.sh`,
  ].join('\n');

export const SETUP_GUIDES: Record<string, SetupGuide> = {
  branding: {
    title: 'Identità del club',
    steps: ['Inserisci nome club, nome app, colori (hex) e carica il logo (PNG trasparente, min 512px).', 'Salva: il backoffice si ricolora subito.'],
    links: [],
    editPath: '/settings',
  },
  domains: {
    title: 'Domini e HTTPS',
    steps: [
      'Crea i record DNS A: app.<dominio> e appadmin.<dominio> verso l\'IP pubblico dell\'istanza EC2 (oppure due host a tua scelta).',
      'Attendi la propagazione (dig +short <host API>, cioè app.<dominio> oppure il tuo --app-host).',
      'Con host personalizzati usa invece: set-domain.sh --app-host <host-api> --admin-host <host-backoffice> <email-per-certbot>.',
      'Lancia il comando qui sotto: imposta gli URL, ottiene i certificati Let\'s Encrypt e passa nginx al vhost di produzione.',
      CLOSE_PORTS_HINT,
    ],
    links: [{ label: 'Verifica DNS', url: 'https://dnschecker.org' }],
    command: ({ slug }) => `sudo /opt/${slug}/bin/set-domain.sh <dominio.it> <email-per-certbot>`,
  },
  'firebase-admin': {
    title: 'Firebase (backend)',
    steps: [
      'Crea un progetto Firebase nuovo per il club (non riusare quello di Teramo).',
      'Authentication → abilita Email/Password, Google, Apple.',
      'Impostazioni progetto → Account di servizio → Genera nuova chiave privata (JSON).',
      'Copia il file JSON sulla EC2 (es. via SSM) in /tmp/firebase-sa.json e lancia il comando. Poi cancella il file.',
    ],
    links: [{ label: 'Console Firebase', url: 'https://console.firebase.google.com/' }],
    command: ({ slug }) => putSecretFromFile(slug, 'FIREBASE_SA_JSON', '/tmp/firebase-sa.json'),
  },
  'firebase-web': {
    title: 'Firebase (backoffice web)',
    steps: [
      'Nel progetto Firebase: Impostazioni progetto → Le tue app → aggiungi app Web.',
      'Copia i 6 valori di firebaseConfig (apiKey, authDomain, projectId, storageBucket, messagingSenderId, appId): sono pubblici.',
      'Authentication → Settings → Domini autorizzati: aggiungi l\'host del backoffice (appadmin.<dominio> oppure il tuo --admin-host).',
      'Lancia il comando (ti chiede i 6 valori) e ricarica questa pagina.',
    ],
    links: [{ label: 'Console Firebase', url: 'https://console.firebase.google.com/' }],
    command: ({ slug }) => `sudo /opt/${slug}/bin/set-firebase-web.sh`,
  },
  'club-admin': {
    title: 'Primo amministratore del club',
    steps: ['Serve Firebase configurato.', 'Vai su Staff, invita l\'amministratore del club con ruolo CLUB_ADMIN.', 'Dopo il suo primo login questa voce diventa verde.'],
    links: [],
    editPath: '/staff',
  },
  ses: {
    title: 'Email (Amazon SES)',
    steps: [
      'Se hai passato -c domain allo stack, aggiungi al DNS i 3 record CNAME DKIM (output DkimRecords).',
      'Attendi la verifica (fino a 72h, di solito minuti).',
      'Usa "Invia email di test" per provare un invio reale.',
      'Se aggiungi il dominio dopo: dopo `cdk deploy` con il dominio, rilancia dal laptop `deploy/bin/adopt-instance.sh <club> <instance-id> --backend-image … --backoffice-image …` (idempotente) per aggiornare mittente e configuration set.',
    ],
    links: [{ label: 'SES identities (eu-central-1)', url: 'https://eu-central-1.console.aws.amazon.com/ses/home?region=eu-central-1#/identities' }],
  },
  s3: {
    title: 'Storage media (S3)',
    steps: ['Creato dallo stack ClubStack-<slug> (bucket <slug>-media-<account>).', 'Se è in errore: verifica che l\'istanza abbia il ruolo IAM dello stack e che AWS_S3_BUCKET sia impostata.'],
    links: [{ label: 'Console S3', url: 'https://s3.console.aws.amazon.com/s3/buckets?region=eu-central-1' }],
  },
  legal: {
    title: 'Privacy e termini',
    steps: ['Apri Pagine legali, compila privacy e termini con il titolare del trattamento del club e pubblica.'],
    links: [],
    editPath: '/settings/legal-pages',
  },
  payments: {
    title: 'Pagamenti (Stripe / PayPal)',
    steps: [
      'Stripe Dashboard del club → Sviluppatori → Chiavi API: copia publishable e secret.',
      'Webhook: endpoint https://<host API>/v1/payments/stripe/webhook/<slug> (host API: app.<dominio> oppure il tuo --app-host; oppure copia l\'URL mostrato in Impostazioni pagamenti), copia il signing secret.',
      'Inserisci tutto in Impostazioni pagamenti (cifrato nel DB).',
    ],
    links: [{ label: 'Stripe Dashboard', url: 'https://dashboard.stripe.com/apikeys' }],
    editPath: '/payment-settings',
  },
  'mobile-links': {
    title: 'Link app mobile (Universal Links / App Links)',
    steps: ['iOS: Team ID + bundle id (es. ABCDE12345.com.club.app).', 'Android: package e SHA-256 della chiave di firma (Play Console → Integrità app).', 'APP_DEEPLINK_SCHEME: lo scheme del deep link dell\'app del club (diverso da quello di altri club).'],
    links: [{ label: 'App Store Connect', url: 'https://appstoreconnect.apple.com/' }, { label: 'Play Console', url: 'https://play.google.com/console' }],
    command: ({ slug }) => `sudo /opt/${slug}/bin/env-set.sh IOS_APP_ID <TEAM.bundle> ANDROID_PACKAGE <package> ANDROID_SHA256_CERTS <sha256> APP_DEEPLINK_SCHEME <scheme>`,
  },
  wallet: { title: 'Apple / Google Wallet', steps: ['Opzionale. Serve un Pass Type ID Apple e un issuer Google Wallet.'], links: [{ label: 'Apple Developer', url: 'https://developer.apple.com/account/resources/identifiers/list/passTypeId' }] },
  sentry: { title: 'Sentry', steps: ['Opzionale. Crea 3 progetti (backend, backoffice, mobile) e copia i DSN.'], links: [{ label: 'Sentry', url: 'https://sentry.io/' }], command: ({ slug }) => `sudo /opt/${slug}/bin/env-set.sh SENTRY_DSN <dsn> BO_SENTRY_DSN <dsn>` },
  anthropic: { title: 'Anthropic (lettura scontrini)', steps: ['Opzionale. Crea una API key dedicata al club.', 'Salvala in un file sulla EC2 e lancia il comando.'], links: [{ label: 'Console Anthropic', url: 'https://console.anthropic.com/settings/keys' }], command: ({ slug }) => putSecretFromFile(slug, 'ANTHROPIC_API_KEY', '/tmp/anthropic.key') },
};
